import { Link, router } from "@inertiajs/react"
import { Info, Plus, TrendingUp } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  IssueSeverity,
  QualityIssueActions,
  QualityIssueCard,
  QualitySuggestedFix,
} from "@/lib/types"

interface Props {
  issue: QualityIssueCard
  actions: QualityIssueActions
  studioUrl: string
  selected: boolean
  onToggleSelect: (id: number) => void
}

function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  const config: Record<IssueSeverity, { label: string; className: string }> = {
    critical: {
      label: "Critical",
      className: "bg-destructive/10 text-destructive border-destructive/30",
    },
    high: {
      label: "High",
      className:
        "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    },
    medium: {
      label: "Medium",
      className: "bg-muted text-muted-foreground border-border",
    },
  }
  const { label, className } = config[severity]
  return (
    <Badge variant="outline" className={className} aria-label={`Severity: ${label}`}>
      <Info className="size-3" aria-hidden />
      <span>{label}</span>
    </Badge>
  )
}

function categoryLabel(fix: QualitySuggestedFix): string {
  const cat = fix.category_id ? `Category #${fix.category_id}` : "Category"
  const tc = fix.time_context ?? "all day"
  return `${cat} · ${String(tc).replace("_", " ")}`
}

const DISMISS_REASONS = [
  { value: "already_covered", label: "Already covered" },
  { value: "out_of_scope", label: "Out of scope" },
  { value: "snooze_30_days", label: "Snooze 30 days" },
] as const

export function CoverageGapCard({
  issue,
  actions,
  studioUrl,
  selected,
  onToggleSelect,
}: Props) {
  const fix: QualitySuggestedFix = issue.suggested_fix ?? {}
  const demand = typeof fix.demand === "number" ? fix.demand : 0
  const currentCount = typeof fix.current_count === "number" ? fix.current_count : 0
  const threshold = typeof fix.threshold === "number" ? fix.threshold : 0

  const [reason, setReason] = useState<(typeof DISMISS_REASONS)[number]["value"]>(
    "out_of_scope",
  )
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleConfirmDismiss = () => {
    router.post(
      actions.dismiss,
      { reason },
      {
        preserveScroll: true,
        onSuccess: () => {
          setDialogOpen(false)
          toast("Coverage gap dismissed", {
            description: `Reason: ${reason.replace("_", " ")}`,
          })
        },
      },
    )
  }

  return (
    <Card
      data-issue-id={issue.id}
      data-issue-type="coverage_gap"
      className={selected ? "ring-2 ring-primary/40" : undefined}
    >
      <CardContent className="space-y-3 py-4">
        <div className="flex items-center gap-2">
          <SeverityBadge severity={issue.severity} />
          <Badge variant="secondary">Coverage gap</Badge>
        </div>

        <div>
          <div className="text-sm font-medium">{categoryLabel(fix)}</div>
          <p className="mt-1 text-sm text-muted-foreground">
            Current pool: <span className="tabular-nums">{currentCount}</span>
            {" of "}
            <span className="tabular-nums">{threshold}</span> recommended POIs in this cell.
          </p>
          {demand > 0 && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <TrendingUp className="size-3" aria-hidden />
              <span>
                {demand} guest views in last 30 days · category is below regional supply
                threshold.
              </span>
            </p>
          )}
        </div>

        <div className="rounded-md border bg-muted/30 p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Action
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild size="sm" className="min-h-9">
              <Link href={studioUrl}>
                <Plus className="mr-1 size-3.5" aria-hidden />
                Create POI to fill gap
              </Link>
            </Button>

            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="min-h-9">
                  Dismiss with reason
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Dismiss coverage gap</AlertDialogTitle>
                  <AlertDialogDescription>
                    This issue will be hidden from the worklist. Choose why so the platform
                    can learn from dismissed signals.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-2">
                  <Select
                    value={reason}
                    onValueChange={(v) =>
                      setReason(v as (typeof DISMISS_REASONS)[number]["value"])
                    }
                  >
                    <SelectTrigger aria-label="Dismiss reason">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISMISS_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleConfirmDismiss}>
                    Confirm dismiss
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(issue.id)}
              aria-label={`Select coverage gap for batch action`}
            />
            <span>Select</span>
          </label>
        </div>
      </CardContent>
    </Card>
  )
}
